import React, { useState } from 'react';
import {
  useMunicipalitiesStatus,
  useMunicipalityStatistics,
  useVerifyMunicipalities,
  useRefreshStaleMunicipalities,
  useVerificationHistory,
} from '@/hooks/api/useAdmin';
import { CardSkeleton, ErrorMessage, TableSkeleton } from '@/components/ui';
import type { MunicipalityStatusResponse } from '@/services/admin.service';

interface MunicipalityManagementProps {
  className?: string;
}

export const MunicipalityManagement: React.FC<MunicipalityManagementProps> = ({
  className = '',
}) => {
  const [staleOnly, setStaleOnly] = useState(false);
  const [selectedCities, setSelectedCities] = useState<Set<string>>(new Set());
  const [showHistory, setShowHistory] = useState(false);

  const {
    data: municipalities,
    isLoading: isLoadingMunicipalities,
    error: municipalitiesError,
    refetch: refetchMunicipalities,
  } = useMunicipalitiesStatus(staleOnly);

  const {
    data: statistics,
    isLoading: isLoadingStatistics,
    error: statisticsError,
  } = useMunicipalityStatistics();

  const { data: history, isLoading: isLoadingHistory } = useVerificationHistory(
    undefined,
    undefined,
    50
  );

  const verifyMutation = useVerifyMunicipalities();
  const refreshMutation = useRefreshStaleMunicipalities();

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'Never';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const handleSelectCity = (city: string) => {
    const newSelected = new Set(selectedCities);
    if (newSelected.has(city)) {
      newSelected.delete(city);
    } else {
      newSelected.add(city);
    }
    setSelectedCities(newSelected);
  };

  const handleSelectAll = () => {
    if (municipalities && selectedCities.size < municipalities.length) {
      setSelectedCities(new Set(municipalities.map((m) => m.city)));
    } else {
      setSelectedCities(new Set());
    }
  };

  const handleVerifySelected = async () => {
    if (selectedCities.size === 0) return;

    try {
      await verifyMutation.mutateAsync({
        cities: Array.from(selectedCities),
        staleOnly: false,
      });
      console.log('Selected municipalities verified successfully');
      setSelectedCities(new Set());
    } catch (err) {
      console.error('Failed to verify municipalities:', err);
    }
  };

  const handleRefreshStale = async () => {
    try {
      await refreshMutation.mutateAsync(90);
      console.log('Stale municipalities refreshed successfully');
    } catch (err) {
      console.error('Failed to refresh stale municipalities:', err);
    }
  };

  const getStatusBadgeColor = (status: string) => {
    switch (status) {
      case 'current':
        return 'bg-green-100 text-green-800';
      case 'stale':
        return 'bg-yellow-100 text-yellow-800';
      case 'error':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getHealthScoreColor = (score: number) => {
    if (score >= 80) return 'text-green-600';
    if (score >= 60) return 'text-yellow-600';
    return 'text-red-600';
  };

  return (
    <div className={`space-y-6 ${className}`}>
      {/* Statistics Card */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <h2 className="text-2xl font-semibold text-gray-900 mb-4">
          Municipality Database Statistics
        </h2>

        {isLoadingStatistics ? (
          <CardSkeleton />
        ) : statisticsError ? (
          <ErrorMessage message="Failed to load statistics" />
        ) : statistics ? (
          <div className="space-y-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-blue-50 rounded-lg p-4">
                <p className="text-sm text-blue-600 font-medium">Total Municipalities</p>
                <p className="text-2xl font-bold text-blue-900">
                  {statistics.total_municipalities}
                </p>
              </div>
              <div className="bg-purple-50 rounded-lg p-4">
                <p className="text-sm text-purple-600 font-medium">Counties Covered</p>
                <p className="text-2xl font-bold text-purple-900">{statistics.coverage_counties.length}</p>
              </div>
              <div className="bg-yellow-50 rounded-lg p-4">
                <p className="text-sm text-yellow-600 font-medium">Stale Data</p>
                <p className="text-2xl font-bold text-yellow-900">
                  {statistics.stale_municipalities}
                </p>
              </div>
              <div className="bg-green-50 rounded-lg p-4">
                <p className="text-sm text-green-600 font-medium">Avg Confidence</p>
                <p className="text-2xl font-bold text-green-900">
                  {(statistics.average_confidence * 100).toFixed(1)}%
                </p>
              </div>
            </div>

            <div className="border-t border-gray-200 pt-4">
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm font-medium text-gray-700">Data Health Score</p>
                <p className={`text-xl font-bold ${getHealthScoreColor(statistics.data_health_score)}`}>
                  {statistics.data_health_score.toFixed(1)}%
                </p>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className={`h-2 rounded-full ${
                    statistics.data_health_score >= 80
                      ? 'bg-green-600'
                      : statistics.data_health_score >= 60
                      ? 'bg-yellow-600'
                      : 'bg-red-600'
                  }`}
                  style={{ width: `${statistics.data_health_score}%` }}
                />
              </div>
            </div>

            {statistics.recommended_actions.length > 0 && (
              <div className="bg-orange-50 border border-orange-200 rounded-md p-4">
                <p className="text-sm font-medium text-orange-800 mb-2">Recommended Actions:</p>
                <ul className="list-disc list-inside space-y-1">
                  {statistics.recommended_actions.map((action, index) => (
                    <li key={index} className="text-sm text-orange-700">
                      {action}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        ) : null}
      </div>

      {/* Municipality Status Table */}
      <div className="bg-white rounded-lg shadow-md">
        <div className="p-6 border-b border-gray-200">
          <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4">
            <h2 className="text-2xl font-semibold text-gray-900">Municipality Status</h2>

            <div className="flex flex-wrap gap-2">
              <label className="flex items-center space-x-2 px-3 py-2 bg-gray-100 rounded-md">
                <input
                  type="checkbox"
                  checked={staleOnly}
                  onChange={(e) => setStaleOnly(e.target.checked)}
                  className="rounded text-indigo-600 focus:ring-indigo-500"
                />
                <span className="text-sm font-medium text-gray-700">Show Stale Only</span>
              </label>

              <button
                onClick={handleVerifySelected}
                disabled={selectedCities.size === 0 || verifyMutation.isPending}
                className="px-4 py-2 bg-indigo-600 text-white rounded-md text-sm font-medium hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {verifyMutation.isPending ? 'Verifying...' : `Verify Selected (${selectedCities.size})`}
              </button>

              <button
                onClick={handleRefreshStale}
                disabled={refreshMutation.isPending}
                className="px-4 py-2 bg-green-600 text-white rounded-md text-sm font-medium hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {refreshMutation.isPending ? 'Refreshing...' : 'Refresh All Stale'}
              </button>

              <button
                onClick={() => setShowHistory(!showHistory)}
                className="px-4 py-2 bg-gray-600 text-white rounded-md text-sm font-medium hover:bg-gray-700"
              >
                {showHistory ? 'Hide History' : 'View History'}
              </button>
            </div>
          </div>
        </div>

        <div className="overflow-x-auto">
          {isLoadingMunicipalities ? (
            <div className="p-6">
              <TableSkeleton rows={5} />
            </div>
          ) : municipalitiesError ? (
            <div className="p-6">
              <ErrorMessage
                message="Failed to load municipality status"
                onRetry={() => refetchMunicipalities()}
              />
            </div>
          ) : municipalities && municipalities.length === 0 ? (
            <div className="p-6 text-center text-gray-500">No municipalities found</div>
          ) : municipalities ? (
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th scope="col" className="px-6 py-3 text-left">
                    <input
                      type="checkbox"
                      checked={selectedCities.size === municipalities.length}
                      onChange={handleSelectAll}
                      className="rounded text-indigo-600 focus:ring-indigo-500"
                    />
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    City
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    State
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    Municipality
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    Status
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    Last Verified
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    Confidence
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    Issues
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {municipalities.map((municipality: MunicipalityStatusResponse) => (
                  <tr key={`${municipality.city}-${municipality.state}`} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <input
                        type="checkbox"
                        checked={selectedCities.has(municipality.city)}
                        onChange={() => handleSelectCity(municipality.city)}
                        className="rounded text-indigo-600 focus:ring-indigo-500"
                      />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {municipality.city}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {municipality.state}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900">{municipality.municipality}</td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`px-2 py-1 text-xs font-semibold rounded-full ${getStatusBadgeColor(municipality.status)}`}
                      >
                        {municipality.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {formatDate(municipality.last_verified)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {municipality.confidence.toFixed(1)}%
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900">
                      {municipality.issues.length > 0 ? (
                        <ul className="list-disc list-inside">
                          {municipality.issues.map((issue, index) => (
                            <li key={index} className="text-red-600">
                              {issue}
                            </li>
                          ))}
                        </ul>
                      ) : (
                        <span className="text-green-600">None</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : null}
        </div>
      </div>

      {/* Verification History */}
      {showHistory && (
        <div className="bg-white rounded-lg shadow-md">
          <div className="p-6 border-b border-gray-200">
            <h3 className="text-xl font-semibold text-gray-900">Verification History</h3>
          </div>

          <div className="overflow-x-auto">
            {isLoadingHistory ? (
              <div className="p-6">
                <TableSkeleton rows={5} />
              </div>
            ) : history && history.results.length > 0 ? (
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th
                      scope="col"
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                    >
                      Municipality
                    </th>
                    <th
                      scope="col"
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                    >
                      City
                    </th>
                    <th
                      scope="col"
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                    >
                      Status
                    </th>
                    <th
                      scope="col"
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                    >
                      Timestamp
                    </th>
                    <th
                      scope="col"
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                    >
                      Response Time
                    </th>
                    <th
                      scope="col"
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                    >
                      Changes
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {history.results.map((entry, index) => (
                    <tr key={index} className="hover:bg-gray-50">
                      <td className="px-6 py-4 text-sm text-gray-900">{entry.municipality}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {entry.city}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span
                          className={`px-2 py-1 text-xs font-semibold rounded-full ${getStatusBadgeColor(entry.status)}`}
                        >
                          {entry.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {formatDate(entry.timestamp)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {entry.response_time_ms}ms
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900">
                        {entry.detected_changes.length > 0 ? (
                          <ul className="list-disc list-inside">
                            {entry.detected_changes.map((change, idx) => (
                              <li key={idx}>{change}</li>
                            ))}
                          </ul>
                        ) : (
                          'None'
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div className="p-6 text-center text-gray-500">No verification history found</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
